<template>
  <ContentCard :loading="loading" :progress="uploadProgress">
    <template #header>
      <ReviewsAgencyTabs />
    </template>
    <template #subheader>
      <ContentCardSubheader :back-to="{ name: ReviewsRouteName.ReviewsAgencyView }">
        {{ t('Reviews.agency.edit.detailsTitle') }}
        <template #actions>
          <button class="btn-secondary-color-s mr-2" data-testid="cancel-button" @click="onCancel">
            {{ t('Reviews.product.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" data-testid="save-button" @click="onSubmit">
            {{ t('Reviews.edit.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>

    <FencingBanner
      v-if="fencedFeatureList.length > 0"
      :headline="t('Reviews.agency.view.media.banner.detailsFencingHeadline')"
      :feature-list="fencedFeatureList"
      :cta-label="t('Reviews.agency.view.media.banner.detailsFencingCtaLabel')"
      :cta-link="t('Reviews.agency.view.media.banner.detailsFencingCtaLink')"
      :banner-image="detailsFencingImageUrl"
      placement="banner"
      entity-type="service"
      :entity-id="agency.id"
    />
    <div class="pt-6">
      <LabelValue :label="t('Reviews.agency.view.title.label').toString()" show-icon>
        <template #hint>
          <span v-html="t('Reviews.agency.edit.title.hint')" />
        </template>
        <div class="grid grid-cols-2 gap-x-6">
          <FieldWrapper for="agency-title" :label="t('Reviews.agency.edit.title.label').toString()">
            <template #default="{ id }">
              <div>
                <InputField :id="id" :model-value="agency.title" data-testid="agency-title" disabled />
              </div>
            </template>
          </FieldWrapper>
        </div>
      </LabelValue>
      <LabelValue :label="t('Reviews.agency.view.category.label').toString()" show-icon>
        <template #hint>
          <span v-html="t('Reviews.agency.edit.category.hint')" />
        </template>
        <div class="grid grid-cols-2 gap-x-6">
          <FieldWrapper for="agency-category" :label="t('Reviews.agency.view.category.label').toString()">
            <template #default="{ id }">
              <div>
                <InputField
                  :id="id"
                  :model-value="agency.categories.map(({ title }) => title).join(', ')"
                  data-testid="agency-category"
                  disabled
                />
              </div>
            </template>
          </FieldWrapper>
        </div>
      </LabelValue>
      <LabelValue :label="t('Reviews.agency.view.logo.label').toString()" show-icon>
        <template #hint>
          <span v-html="t('Reviews.agency.view.logo.hint')" />
        </template>
        <MediaUpload
          data-testid="agency-logo-upload"
          :accept="LOGO_MIME_TYPES.join(', ')"
          :preview-asset="agency.logo || undefined"
          :short-hint="shortLogoHint"
          :error="getValidationErrors(v$.logo)"
          @upload="handleLogoUpload"
        >
          <template #preview="previewProps">
            <ProductLogo :logo-url="previewProps.url" size="large" />
          </template>
        </MediaUpload>
      </LabelValue>
      <FeatureFencingWrapper
        :fenced="heroImageFenced"
        :label="t('Reviews.agency.view.detailsEdit.heroFencingLabel')"
        :help-tooltip="t('Reviews.agency.view.detailsEdit.heroFencingTooltip')"
        :help-link="t('Reviews.agency.view.detailsEdit.heroFencingHelpLink')"
        :upgrade-cta="t('Reviews.agency.view.detailsEdit.heroFencingUpgradeCta')"
        :upgrade-cta-link="t('Reviews.agency.view.detailsEdit.heroFencingUpgradeCtaLink')"
        placement="hero"
        entity-type="service"
        :entity-id="agency.id"
      >
        <LabelValue
          :label="t('Reviews.agency.view.hero.label').toString()"
          :class="{ 'text-disabled': heroImageFenced }"
          :show-icon="!heroImageFenced"
        >
          <template v-if="!heroImageFenced" #hint>
            <span v-html="t('Reviews.agency.edit.hero.hint')" />
          </template>
          <MediaUpload
            data-testid="agency-hero-upload"
            :accept="HERO_MIME_TYPES.join(', ')"
            aspect-ratio="16:9"
            :disabled="heroImageFenced"
            max-width="648px"
            :preview-asset="heroImageFenced ? undefined : agency.hero || undefined"
            :short-hint="shortHeroHint"
            :error="getValidationErrors(v$.hero)"
            @upload="handleHeroUpload"
          />
        </LabelValue>
      </FeatureFencingWrapper>
      <LabelValue
        v-if="heroIsVideo"
        data-testid="agency-hero-video-thumbnail-field"
        :label="t('Reviews.agency.view.heroVideoThumbnail.label').toString()"
      >
        <MediaUpload
          data-testid="agency-hero-video-thumbnail-upload"
          :accept="HERO_VIDEO_THUMBNAIL_MIME_TYPES.join(', ')"
          aspect-ratio="16:9"
          :disabled="heroImageFenced"
          max-width="648px"
          :preview-asset="heroImageFenced ? undefined : agency.heroVideoThumbnail || undefined"
          :short-hint="shortHeroVideoThumbnailHint"
          :error="getValidationErrors(v$.heroVideoThumbnail)"
          @upload="handleHeroVideoThumbnailUpload"
        />
      </LabelValue>
      <LabelValue :label="t('Reviews.agency.view.locations.label').toString()" show-icon>
        <template #hint>
          <span v-html="t('Reviews.agency.edit.locations.hint')" />
        </template>
        <div class="grid grid-cols-3 gap-6 pt-6">
          <div class="col-span-1 flex flex-col">
            <AutocompleteField
              id="locations-autocomplete"
              class="mb-6"
              :label="t('Reviews.agency.edit.locations.cities.label').toString()"
              :placeholder="t('Reviews.agency.edit.locations.cities.placeholder')"
              :options="locationsOptions"
              :selected="formData.locations"
              @update="updateSelectedLocations"
              @search="updateLocationSearchTerm($event)"
            />
            <CheckBox v-model="v$.remote.$model" data-testid="remote-checkbox">
              {{ t('Reviews.agency.edit.locations.remote.label').toString() }}
            </CheckBox>
          </div>
          <div class="col-span-2 rounded-md bg-surface-elevation-2 p-8" data-testid="locations-preview">
            <ChipBadge class="mb-4" color="yellow" rounded> {{ t('Reviews.badge.preview') }} </ChipBadge>
            <div class="border-solid border-main bg-surface p-6">
              <div class="mb-4 text-headline-xs">{{ t('Reviews.agency.edit.locations.preview.label') }}</div>
              <ChipBadge v-if="formData.remote" class="mr-2" rounded color="default">
                {{ t('Reviews.agency.edit.locations.remote.label') }}
              </ChipBadge>
              <ChipBadge v-for="(location, idx) in locationsPreview" :key="idx" class="mt-2 mr-2" color="purple" rounded size="m">
                {{ location }}
              </ChipBadge>
            </div>
          </div>
        </div>
      </LabelValue>
      <LabelValue :label="t('Reviews.agency.view.description.label').toString()">
        <template #hint>
          <span v-html="t('Reviews.agency.edit.description.hint')" />
        </template>
        <div class="mb-8 flex gap-x-4">
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex-1">
            <VuelidateField :validation="v$.shortDescription[lang]">
              <template #default="{ error, state, countHint }">
                <FieldWrapper :for="`short-description-${lang}`" :error="error" :count-hint="countHint">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <span>{{ t('Reviews.agency.view.shortDescription.label', { lang }) }}</span>
                      <ChipBadge class="uppercase" color="purple" size="s" rounded>{{ lang }}</ChipBadge>
                    </div>
                  </template>
                  <template #default="{ id }">
                    <div class="mt-2">
                      <TextareaField
                        :id="id"
                        v-model="v$.shortDescription[lang].$model"
                        :data-testid="`short-description-${lang}`"
                        :state="state"
                        :maxlength="SHORT_DESCRIPTION_MAX_LENGTH"
                        rows="3"
                        :placeholder="
                          t('Reviews.agency.edit.shortDescription.placeholder', {
                            lang,
                            maxLength: SHORT_DESCRIPTION_MAX_LENGTH,
                          }).toString()
                        "
                        @keydown.enter.prevent
                      />
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
        </div>
        <FeatureFencingWrapper
          :fenced="descriptionLongFenced"
          :label="t('Reviews.agency.view.detailsEdit.descriptionLongFencingLabel')"
          :help-tooltip="t('Reviews.agency.view.detailsEdit.descriptionLongFencingTooltip')"
          :help-link="t('Reviews.agency.view.detailsEdit.descriptionLongFencingHelpLink')"
          :upgrade-cta="t('Reviews.agency.view.detailsEdit.descriptionLongFencingUpgradeCta')"
          :upgrade-cta-link="t('Reviews.agency.view.detailsEdit.descriptionLongFencingUpgradeCtaLink')"
          placement="description_long"
          entity-type="service"
          :entity-id="agency.id"
          data-testid="description-long-fencing"
        >
          <div class="grid grid-cols-2 gap-x-4">
            <div v-for="lang in ALLOWED_LOCALES" :key="lang">
              <div class="flex items-center justify-between">
                <div
                  class="mb-1 text-label-m-bold"
                  :class="{ 'text-disabled': descriptionLongFenced }"
                  :data-testid="`description-label-${lang}`"
                >
                  {{ t('Reviews.agency.edit.longDescription.label', { lang }) }}
                </div>
                <ChipBadge
                  class="uppercase"
                  :color="descriptionLongFenced ? 'default' : 'purple'"
                  :type="descriptionLongFenced ? 'outlined' : 'default'"
                  size="s"
                  rounded
                  :data-testid="`description-locale-${lang}`"
                >
                  <span :class="{ 'text-disabled': descriptionLongFenced }">{{ lang }}</span>
                </ChipBadge>
              </div>
              <MarkdownContent
                v-if="descriptionLongFenced"
                class="text-disabled"
                :md="agency.descriptionTranslationsMap[lang] || ''"
                :data-testid="`description-readonly-${lang}`"
              />
              <MarkdownInput
                v-else
                :id="`description-${lang}`"
                :key="lang"
                v-model="v$.description[lang].$model"
                :error="getValidationErrors(v$.description[lang])"
                dense
                :allowed-tags="['h3', 'h4', 'h5', 'p']"
                :data-testid="`description-${lang}`"
                :placeholder="t('Reviews.agency.edit.longDescription.placeholder', { lang })"
              />
            </div>
          </div>
        </FeatureFencingWrapper>
      </LabelValue>
      <LabelValue :label="t('Reviews.agency.view.links.label').toString()" show-icon>
        <template #hint>
          {{ t('Reviews.agency.edit.url.hint') }}
        </template>
        <div class="grid grid-cols-2 gap-x-6">
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex items-center">
            <VuelidateField :validation="v$.url[lang]">
              <template #default="{ error, state }">
                <FieldWrapper :for="`website-url-${lang}`" :error="error" hint=" ">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <span>{{ t('Reviews.agency.edit.url.label', { lang }) }}</span>
                      <ChipBadge class="uppercase" color="purple" size="s" rounded>{{ lang }}</ChipBadge>
                    </div>
                  </template>
                  <template #default="{ id }">
                    <div class="mt-2">
                      <InputField
                        :id="id"
                        v-model="v$.url[lang].$model"
                        type="text"
                        :state="state"
                        :data-testid="`website-url-${lang}`"
                        :placeholder="t('Reviews.agency.edit.url.placeholder', { lang }).toString()"
                      >
                        <template #prepend>
                          <GlobeIcon class="lucide-icon-s" />
                        </template>
                      </InputField>
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
          <div v-for="lang in ALLOWED_LOCALES" :key="lang" class="flex items-center">
            <VuelidateField :validation="v$.linkText[lang]">
              <template #default="{ error, state }">
                <FieldWrapper :for="`website-link-text-${lang}`" :error="error" hint=" ">
                  <template #label>
                    <div class="flex items-center justify-between">
                      <span>{{ t('Reviews.agency.edit.web.label', { lang }) }}</span>
                      <ChipBadge class="uppercase" color="purple" size="s" rounded>{{ lang }}</ChipBadge>
                    </div>
                  </template>
                  <template #default="{ id }">
                    <div class="mt-2">
                      <InputField
                        :id="id"
                        v-model="v$.linkText[lang].$model"
                        type="text"
                        :state="state"
                        :data-testid="`website-link-text-${lang}`"
                        :placeholder="t('Reviews.agency.edit.web.placeholder', { lang }).toString()"
                      >
                        <template #prepend>
                          <TypeIcon class="lucide-icon-s" />
                        </template>
                      </InputField>
                    </div>
                  </template>
                </FieldWrapper>
              </template>
            </VuelidateField>
          </div>
          <FeatureFencingWrapper
            :fenced="socialLinksFenced"
            :label="t('Reviews.agency.view.detailsEdit.linksFencingLabel')"
            :help-tooltip="t('Reviews.agency.view.detailsEdit.linksFencingTooltip')"
            :help-link="t('Reviews.agency.view.detailsEdit.linksFencingHelpLink')"
            :upgrade-cta="t('Reviews.agency.view.detailsEdit.linksFencingUpgradeCta')"
            :upgrade-cta-link="t('Reviews.agency.view.detailsEdit.linksFencingUpgradeCtaLink')"
            placement="social_links"
            entity-type="service"
            :entity-id="agency.id"
            :class="{ 'col-span-2': socialLinksFenced }"
          >
            <div class="flex items-center" :class="{ 'w-1/2 pr-3 text-disabled': socialLinksFenced }">
              <VuelidateField :validation="v$.linkedin">
                <template #default="{ error, state }">
                  <FieldWrapper for="linkedin-link" label="LinkedIn" :error="error" hint=" " optional>
                    <template #default="{ id }">
                      <div class="mt-2">
                        <InputField
                          :id="id"
                          v-model="v$.linkedin.$model"
                          type="text"
                          :state="state"
                          :disabled="socialLinksFenced"
                          data-testid="linkedin-link"
                          :placeholder="t('Reviews.agency.edit.linkedin.placeholder').toString()"
                        >
                          <template #prepend>
                            <LinkedinCustomIcon class="lucide-icon-s" />
                          </template>
                        </InputField>
                      </div>
                    </template>
                  </FieldWrapper>
                </template>
              </VuelidateField>
            </div>
            <div />
            <div class="flex items-center" :class="{ 'w-1/2 pr-3 text-disabled': socialLinksFenced }">
              <VuelidateField :validation="v$.twitter">
                <template #default="{ error, state }">
                  <FieldWrapper for="twitter-link" label="X" :error="error" hint=" " optional>
                    <template #default="{ id }">
                      <div class="mt-2">
                        <InputField
                          :id="id"
                          v-model="v$.twitter.$model"
                          type="text"
                          :state="state"
                          :disabled="socialLinksFenced"
                          data-testid="twitter-link"
                          :placeholder="t('Reviews.agency.edit.twitter.placeholder').toString()"
                        >
                          <template #prepend>
                            <XCustomIcon class="lucide-icon-s" />
                          </template>
                        </InputField>
                      </div>
                    </template>
                  </FieldWrapper>
                </template>
              </VuelidateField>
            </div>
          </FeatureFencingWrapper>
        </div>
      </LabelValue>
      <ReviewsCtaEditForm
        :data="agency"
        :entity-id="agency.id"
        :hint="t('Reviews.agency.edit.contact.primary.hint')"
        subject="agency"
        @change="handleCtaUpdate"
      />
    </div>
    <template #footer>
      <div class="my-6 flex flex-row items-center justify-end">
        <button class="btn-secondary-color-s mr-2" data-testid="agency-details-foot-cancel" @click="onCancel">
          {{ t('Reviews.edit.action.cancel') }}
        </button>
        <button class="btn-primary-color-s" data-testid="agency-details-foot-save" @click="onSubmit">
          {{ t('Reviews.edit.action.save') }}
        </button>
      </div>
    </template>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
</template>
<script setup lang="ts">
import { isNotNull } from '@ramp106/omrjs-common'
import { ChipBadge, AutocompleteField, CheckBox, FieldWrapper, InputField, MarkdownContent, TextareaField } from '@ramp106/omrjs-core-ui'
import { MarkdownInput } from '@ramp106/omrjs-core-ui/deprecated'
import { GlobeIcon, LinkedinCustomIcon, TypeIcon, XCustomIcon } from '@ramp106/omrjs-core-ui/icons'
import { ProductLogo } from '@ramp106/omrjs-reviews-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { useVuelidate, type ValidationArgs } from '@vuelidate/core'
import TurnDown from 'turndown'
import { computed, inject, reactive, ref, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { ReviewsAgencyTabs } from '@/components/ReviewsAgenciesManagements'
import detailsFencingImageUrl from '@/components/ReviewsCommon/Fencing/details_fencing.png'
import FeatureFencingWrapper from '@/components/ReviewsCommon/Fencing/FeatureFencingWrapper.vue'
import FencingBanner from '@/components/ReviewsCommon/Fencing/FencingBanner.vue'
import type { CtaFormData, ReviewsAgencyEditFormData } from '@/components/ReviewsManagement'
import {
  LOGO_MAX_SIZE_MB,
  LOGO_MIME_TYPES,
  HERO_MAX_SIZE_MB,
  HERO_MIME_TYPES,
  HERO_VIDEO_THUMBNAIL_MAX_SIZE_MB,
  HERO_VIDEO_THUMBNAIL_MIME_TYPES,
} from '@/components/ReviewsManagement/constants.ts'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import ReviewsCtaEditForm from '@/components/ReviewsManagement/ReviewsCtaEditForm.vue'
import {
  AgencyConfigCtaPrimaryVendorEnum,
  type AgencyData,
  type ManageAgencyAttributes,
  type TranslationAttributes,
  useReviewsAgencyOptionsQuery,
  useUpdateReviewsAgencyMutation,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper.ts'
import { ALLOWED_LOCALES, areTranslationsChanged } from '@/helpers/translationsHelper.ts'
import {
  buildTranslationFieldsRules,
  fileMaxSizeRule,
  fileMimeTypeRule,
  getValidationErrors,
  imageMaxDimensionRule,
  imageMinDimensionRule,
  urlRule,
  linkedinUrlRule,
} from '@/helpers/validationHelper.ts'
import { ReviewsRouteName } from '@/router/types.ts'
import { maxLength } from '@/services/validators.ts'
import { reloadReviewsAgencyKey } from '@/symbols/reviewsReloadKeys'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import LabelValue from '@/ui/LabelValue.vue'
import MediaUpload from '@/ui/MediaUpload.vue'
import VuelidateField from '@/ui/VuelidateField.vue'

const SHORT_DESCRIPTION_MAX_LENGTH = 180

const props = defineProps<{
  agency: AgencyData
}>()

const reloadReviewsAgency = inject(reloadReviewsAgencyKey) as () => void

const { t } = useI18n()
const router = useRouter()

const heroImageFenced = computed(() => !props.agency.configFlags.heroEditable)
const socialLinksFenced = computed(() => !props.agency.configFlags.socialLinksAllowed)
const descriptionLongFenced = computed(() => !props.agency.configFlags.descriptionLongEditable)

const featureMap = [
  { flag: !props.agency.configFlags.heroEditable, key: 'Reviews.agency.view.media.banner.headerImageFencing' },
  { flag: !props.agency.configFlags.socialLinksAllowed, key: 'Reviews.agency.view.media.banner.socialLinksFencing' },
  { flag: !props.agency.configFlags.ctaPrimaryEditable, key: 'Reviews.agency.view.media.banner.ctaPrimaryFencing' },
  { flag: !props.agency.configFlags.ctaSecondaryEditable, key: 'Reviews.agency.view.media.banner.ctaSecondaryFencing' },
  { flag: !props.agency.configFlags.descriptionLongEditable, key: 'Reviews.agency.view.media.banner.descriptionLongFencing' },
]
const fencedFeatureList = computed(() => featureMap.filter((feature) => feature.flag).map((feature) => t(feature.key)))

const backTo = { name: ReviewsRouteName.ReviewsAgencyViewDetails, params: { agencyId: props.agency.id } }
const uploadProgress = ref(0)
const locationSearchTerm = ref('')
const confirmCloseModal = useTemplateRef('confirmCloseModal')

const shortLogoHint = computed(() => {
  const acceptedMimeTypes = LOGO_MIME_TYPES.map((type) => `${type.split('/')[1]}`).join(', ')

  return [acceptedMimeTypes, `Max. ${LOGO_MIME_TYPES} MB`]
})

const shortHeroHint = computed(() => {
  const acceptedMimeTypes = HERO_MIME_TYPES.map((type) => `${type.split('/')[1]}`).join(', ')

  return [acceptedMimeTypes, `Max. ${HERO_MAX_SIZE_MB} MB`]
})

const shortHeroVideoThumbnailHint = computed(() => {
  const acceptedMimeTypes = HERO_VIDEO_THUMBNAIL_MIME_TYPES.map((type) => `${type.split('/')[1]}`).join(', ')

  return [acceptedMimeTypes, `Max. ${HERO_VIDEO_THUMBNAIL_MAX_SIZE_MB} MB`]
})

const formData = reactive<ReviewsAgencyEditFormData>({
  description: { ...props.agency.descriptionTranslationsMap },
  shortDescription: { ...props.agency.shortDescriptionTranslationsMap },
  url: { ...props.agency.urlTranslationsMap },
  linkText: { ...props.agency.linkTextTranslationsMap },
  linkedin: props.agency.linkedin || null,
  twitter: props.agency.twitter || null,
  remote: props.agency.remote,
  locations: props.agency.locations.map((l) => l.citySlug || null).filter(isNotNull),
  logo: null,
  hero: null,
  heroVideoThumbnail: null,
  ctaPrimaryEnabled: props.agency.configFlags.ctaPrimaryActive,
  ctaSecondaryLabel: { ...props.agency.ctaSecondaryLabelTranslationsMap },
  ctaSecondaryUrl: { ...props.agency.ctaSecondaryUrlTranslationsMap },
})

const heroIsVideo = computed(() => {
  if (formData.hero) {
    return formData.hero.type.startsWith('video')
  }

  return props.agency.hero?.contentType?.startsWith('video') ?? false
})

const rules = computed<ValidationArgs<ReviewsAgencyEditFormData>>(() => ({
  description: buildTranslationFieldsRules({}),
  shortDescription: buildTranslationFieldsRules({ maxLength: maxLength(SHORT_DESCRIPTION_MAX_LENGTH) }),
  linkText: buildTranslationFieldsRules({}),
  url: buildTranslationFieldsRules({ urlFormat: urlRule(t) }),
  linkedin: { urlFormat: linkedinUrlRule(t) },
  twitter: { urlFormat: urlRule(t) },
  logo: {
    fileSize: fileMaxSizeRule(t, { megaBytes: LOGO_MAX_SIZE_MB }),
    fileType: fileMimeTypeRule(t, { mimeTypes: LOGO_MIME_TYPES }),
    minDimension: imageMinDimensionRule(t, { minWidth: 64, minHeight: 64 }),
    maxDimension: imageMaxDimensionRule(t, { maxWidth: 1024, maxHeight: 1024 }),
  },
  hero: {
    fileSize: fileMaxSizeRule(t, { megaBytes: HERO_MAX_SIZE_MB }),
    fileType: fileMimeTypeRule(t, { mimeTypes: HERO_MIME_TYPES }),
    minDimension: imageMinDimensionRule(t, { minWidth: 646, minHeight: 364 }),
  },
  heroVideoThumbnail: {
    fileSize: fileMaxSizeRule(t, { megaBytes: HERO_VIDEO_THUMBNAIL_MAX_SIZE_MB }),
    fileType: fileMimeTypeRule(t, { mimeTypes: HERO_VIDEO_THUMBNAIL_MIME_TYPES }),
    minDimension: imageMinDimensionRule(t, { minWidth: 646, minHeight: 364 }),
  },
  locations: {},
  remote: {},
  ctaPrimaryEnabled: {},
  ctaSecondaryLabel: {},
  ctaSecondaryUrl: {},
}))

const v$ = useVuelidate(rules, formData)

function handleLogoUpload(file: File) {
  v$.value.logo.$model = file
}

function handleHeroUpload(file: File) {
  v$.value.hero.$model = file
}

function handleHeroVideoThumbnailUpload(file: File) {
  v$.value.heroVideoThumbnail.$model = file
}

watch(heroIsVideo, (isVideo) => {
  if (!isVideo) {
    v$.value.heroVideoThumbnail.$model = null
  }
})

const handleCtaUpdate = (data: CtaFormData) => {
  v$.value.ctaPrimaryEnabled.$model = data.ctaPrimaryEnabled
  v$.value.ctaSecondaryUrl.$model = { ...data.ctaSecondaryUrl }
  v$.value.ctaSecondaryLabel.$model = { ...data.ctaSecondaryLabel }
}

const { result: agencyOptions } = useReviewsAgencyOptionsQuery({
  clientId: 'reviews',
})

const availableLocations = computed(() => {
  const options: { text: string; value: string }[] = []
  if (!agencyOptions.value?.manage?.options.cities) {
    return options
  }

  agencyOptions.value.manage.options.cities.forEach((city) => {
    if (city.name && city.slug) {
      options.push({
        text: city.name,
        value: city.slug,
      })
    }
  })

  return options
})

const locationsOptions = computed(() => {
  if (locationSearchTerm.value) {
    return availableLocations.value.filter((option) => option.text.toLowerCase().includes(locationSearchTerm.value.toLowerCase()))
  }

  return availableLocations.value
})

const locationsPreview = computed(() => {
  const locations: string[] = []
  formData.locations.forEach((location) => {
    const city = availableLocations.value?.find((city) => city.value === location)
    if (city) {
      locations.push(city.text)
    }
  })

  return locations
})

function updateSelectedLocations({ checkedOptions }: { checkedOptions: string[] }) {
  v$.value.locations.$model = checkedOptions
}

function updateLocationSearchTerm(searchTerm: string) {
  locationSearchTerm.value = searchTerm
}

const {
  mutate: mutateAgency,
  loading: loadingUpdateAgency,
  onDone,
  onError,
} = useUpdateReviewsAgencyMutation({
  clientId: 'reviews',
  context: {
    fetchOptions: {
      useUpload: true,
      onProgress: (progress: ProgressEvent) => {
        uploadProgress.value = Math.round((progress.loaded / progress.total) * 100)
      },
    },
  },
})

const loading = computed(() => loadingUpdateAgency.value)

function onCancel() {
  track('profile_update_cancel', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  if (v$.value.$anyDirty) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo)
  }
}

function prepareAgencyLocationAttributes() {
  const agencyLocationsAttributes: ManageAgencyAttributes['locationsAttributes'] = []

  props.agency.locations.forEach((location) => {
    const stillExists = formData.locations.find((keep) => keep === location.citySlug)
    if (!stillExists) {
      agencyLocationsAttributes.push({
        id: location.id,
        _destroy: true,
      })
    }
  })
  formData.locations.forEach((formLocation) => {
    const existingLocation = props.agency.locations.find((agencyLocation) => formLocation === agencyLocation.citySlug)
    if (!existingLocation) {
      agencyLocationsAttributes.push({
        city: formLocation,
      })
    }
  })

  return agencyLocationsAttributes
}

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  const agencyLocationAttributes = prepareAgencyLocationAttributes()

  v$.value.$touch()
  if (v$.value.$invalid) {
    showNotification(t('Reviews.agency.notifications.invalidData'), 'error')
    return
  }

  try {
    const uploadedHeroImage = formData.hero && formData.hero.type.startsWith('image')
    const uploadedHeroVideo = formData.hero && formData.hero.type.startsWith('video')

    let heroImage
    let heroVideo
    if (uploadedHeroImage) {
      heroImage = formData.hero
      heroVideo = null
    }
    if (uploadedHeroVideo) {
      heroVideo = formData.hero
      heroImage = null
    }

    const turnDown = new TurnDown({
      emDelimiter: '_',
      linkStyle: 'inlined',
      headingStyle: 'atx',
    })

    const descriptionTranslations: TranslationAttributes[] = []
    const shortDescriptionTranslations: TranslationAttributes[] = []
    const linkTextTranslations: TranslationAttributes[] = []
    const urlTranslations: TranslationAttributes[] = []

    ALLOWED_LOCALES.forEach((locale) => {
      if (v$.value.description[locale]?.$dirty && v$.value.description[locale].$model !== props.agency.descriptionTranslationsMap[locale]) {
        descriptionTranslations.push({
          locale,
          value: formData.description[locale] ? turnDown.turndown(formData.description[locale]) : '',
        })
      }

      shortDescriptionTranslations.push({
        locale,
        value: formData.shortDescription[locale],
      })

      linkTextTranslations.push({
        locale,
        value: formData.linkText[locale],
      })

      urlTranslations.push({
        locale,
        value: formData.url[locale],
      })
    })

    const attributes: ManageAgencyAttributes = {
      logo: formData.logo ? formData.logo : undefined,
      heroImage,
      heroVideo,
      heroVideoThumbnail: formData.heroVideoThumbnail ? formData.heroVideoThumbnail : undefined,
      shortDescriptionTranslations,
      linkTextTranslations,
      urlTranslations,
      remote: formData.remote,
      locationsAttributes: agencyLocationAttributes,
      configAttributes: {},
    }

    if (props.agency.configFlags.descriptionLongEditable && descriptionTranslations.length > 0) {
      attributes.descriptionTranslations = descriptionTranslations
    }

    if (v$.value.ctaPrimaryEnabled.$dirty) {
      attributes.configAttributes = {
        ...attributes.configAttributes,
        ctaPrimaryVendor: formData.ctaPrimaryEnabled ? AgencyConfigCtaPrimaryVendorEnum.Automatic : AgencyConfigCtaPrimaryVendorEnum.No,
      }
    }

    if (props.agency.configFlags.socialLinksAllowed) {
      if (formData.linkedin !== (props.agency.linkedin || null)) {
        attributes.linkedin = formData.linkedin
      }
      if (formData.twitter !== (props.agency.twitter || null)) {
        attributes.twitter = formData.twitter
      }
    }

    if (
      props.agency.configFlags.ctaSecondaryEditable &&
      (areTranslationsChanged(formData.ctaSecondaryLabel, props.agency.ctaSecondaryLabelTranslationsMap) ||
        areTranslationsChanged(formData.ctaSecondaryUrl, props.agency.ctaSecondaryUrlTranslationsMap))
    ) {
      const ctaSecondaryLabelTranslations: TranslationAttributes[] = []
      const ctaSecondaryUrlTranslations: TranslationAttributes[] = []
      ALLOWED_LOCALES.forEach((locale) => {
        ctaSecondaryLabelTranslations.push({
          locale,
          value: formData.ctaSecondaryLabel[locale],
        })
        ctaSecondaryUrlTranslations.push({
          locale,
          value: formData.ctaSecondaryUrl[locale],
        })
      })
      attributes.ctaSecondaryLabelTranslations = ctaSecondaryLabelTranslations
      attributes.ctaSecondaryUrlTranslations = ctaSecondaryUrlTranslations
    }

    await mutateAgency({
      id: props.agency.id,
      attributes,
    })
  } catch {
    uploadProgress.value = 0
  }
}

onDone(() => {
  uploadProgress.value = 0
  reloadReviewsAgency()
  showNotification(t('Reviews.agency.notifications.savedSuccessfully', { name: props.agency.title }), 'success')
  router.push(backTo)
})

onError((error) => showError(error))

const showError = (error: Error) => {
  uploadProgress.value = 0
  if (error) showNotification(error.message, 'error')
}
</script>
